import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AgencyPaymentsService from 'services/AgencyPaymentsService';
import dayjs from 'dayjs';

// Async thunks
export const fetchAgencyPayments = createAsyncThunk(
  'agencyPayments/fetchAgencyPayments',
  async (filters = {}) => {
    const response = await AgencyPaymentsService.fetch(filters);
    return response.data;
  }
);

export const createAgencyPayment = createAsyncThunk(
  'agencyPayments/createAgencyPayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await AgencyPaymentsService.create(paymentData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error creating payment');
    }
  }
);

// Add update thunk
export const updateAgencyPayment = createAsyncThunk(
  'agencyPayments/updateAgencyPayment',
  async ({ id, paymentData }, { rejectWithValue }) => {
    try {
      const response = await AgencyPaymentsService.update(id, paymentData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error updating payment');
    }
  }
);

const currentMonth = dayjs().format('MMM YYYY');

const agencyPaymentsSlice = createSlice({
  name: 'agencyPayments',
  initialState: {
    payments: [],
    loading: false,
    updating: false, // Add updating state
    error: null,
    filters: {
      agency_name: '',
      month: currentMonth
    },
    editingPayment: null // Track which payment is being edited
  },
  reducers: {
    setAgencyPaymentsFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearAgencyPaymentsFilters: (state) => {
      state.filters = {
        agency_name: '',
        month: currentMonth
      };
    },
    setEditingPayment: (state, action) => {
      state.editingPayment = action.payload;
    },
    clearEditingPayment: (state) => {
      state.editingPayment = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch agency payments
      .addCase(fetchAgencyPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgencyPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload;
      })
      .addCase(fetchAgencyPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Create agency payment
      .addCase(createAgencyPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAgencyPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.payments.unshift(action.payload);
      })
      .addCase(createAgencyPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      })
      // Update agency payment
      .addCase(updateAgencyPayment.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateAgencyPayment.fulfilled, (state, action) => {
        state.updating = false;
        // Update the payment in the list
        const index = state.payments.findIndex(payment => payment.id === action.payload.id);
        if (index !== -1) {
          state.payments[index] = action.payload;
        }
        state.editingPayment = null;
      })
      .addCase(updateAgencyPayment.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload?.message || action.error.message;
      });
  }
});

export const { 
  setAgencyPaymentsFilters, 
  clearAgencyPaymentsFilters, 
  setEditingPayment, 
  clearEditingPayment 
} = agencyPaymentsSlice.actions;
export default agencyPaymentsSlice.reducer;